import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    Paper
} from '@mui/material';
import { FaTimes, FaExchangeAlt } from 'react-icons/fa';
import { diffLines, diffWords } from 'diff';
import AiAgentService from '../../../api/AiAgentService';
import useFormatDate from '../../../hooks/useFormatDate';

const PromptComparison = ({ open, onClose, baseVersion, promptId }) => {
    const { formatDateTime } = useFormatDate();
    const [versions, setVersions] = useState([]);
    const [compareVersion, setCompareVersion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [diffResult, setDiffResult] = useState([]);
    const [viewMode, setViewMode] = useState('line'); // 'line' або 'word'

    // Завантаження версій для порівняння
    const loadVersions = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = {
                versions_page: 1,
                versions_page_size: 50 // Завантажуємо більше версій для вибору
            };
            
            const response = await AiAgentService.getPromptWithVersions(params);
            
            if (response?.versions) {
                // Фільтруємо базову версію зі списку
                const filteredVersions = response.versions.filter(
                    v => v.id !== baseVersion?.id
                );
                setVersions(filteredVersions);
            }
        } catch (err) {
            setError('Помилка завантаження версій: ' + (err.message || 'Невідома помилка'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && promptId) {
            loadVersions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, promptId]);

    // Виконання порівняння
    useEffect(() => {
        if (baseVersion && compareVersion) {
            const baseContent = baseVersion.prompt_content || '';
            const compareContent = compareVersion.prompt_content || '';
            
            let differences;
            if (viewMode === 'line') {
                differences = diffLines(baseContent, compareContent);
            } else {
                differences = diffWords(baseContent, compareContent);
            }
            
            setDiffResult(differences);
        }
    }, [baseVersion, compareVersion, viewMode]);

    const handleVersionSelect = (versionId) => {
        const selected = versions.find(v => v.id === versionId);
        setCompareVersion(selected);
    };

    const handleSwapVersions = () => {
        if (compareVersion && baseVersion) {
            const temp = compareVersion;
            setCompareVersion(baseVersion);
            // Оновлюємо базову версію через колбек
            if (onClose) {
                onClose(temp);
            }
        }
    };

    const renderDiff = () => {
        if (!compareVersion) {
            return (
                <Alert severity="info">
                    Оберіть версію для порівняння
                </Alert>
            );
        }

        return (
            <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                        variant={viewMode === 'line' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setViewMode('line')}
                    >
                        По рядках
                    </Button>
                    <Button
                        variant={viewMode === 'word' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setViewMode('word')}
                    >
                        По словах
                    </Button>
                </Box>

                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 2, 
                        backgroundColor: '#f5f5f5',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        lineHeight: 1.6
                    }}
                >
                    {diffResult.map((part, index) => {
                        let backgroundColor = 'transparent';
                        let color = 'inherit';
                        let textDecoration = 'none';
                        let padding = '0';
                        let display = 'inline';

                        if (part.added) {
                            backgroundColor = '#e6ffed';
                            color = '#24292e';
                            padding = '2px 4px';
                            display = viewMode === 'line' ? 'block' : 'inline';
                        } else if (part.removed) {
                            backgroundColor = '#ffeef0';
                            color = '#24292e';
                            textDecoration = 'line-through';
                            padding = '2px 4px';
                            display = viewMode === 'line' ? 'block' : 'inline';
                        } else {
                            display = viewMode === 'line' ? 'block' : 'inline';
                        }

                        return (
                            <span
                                key={index}
                                style={{
                                    backgroundColor,
                                    color,
                                    textDecoration,
                                    padding,
                                    display,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {part.value}
                            </span>
                        );
                    })}
                </Paper>

                {/* Легенда */}
                <Box sx={{ mt: 2, display: 'flex', gap: 3, fontSize: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                            sx={{ 
                                width: 16, 
                                height: 16, 
                                backgroundColor: '#ffeef0',
                                border: '1px solid #ddd'
                            }} 
                        />
                        <Typography variant="caption">Видалено з базової версії</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                            sx={{ 
                                width: 16, 
                                height: 16, 
                                backgroundColor: '#e6ffed',
                                border: '1px solid #ddd'
                            }} 
                        />
                        <Typography variant="caption">Додано в порівнюваній версії</Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={() => onClose(null)} 
            maxWidth="xl" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '90vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Порівняння версій промпту</Typography>
                    <Button
                        startIcon={<FaTimes />}
                        onClick={() => onClose(null)}
                        size="small"
                    >
                        Закрити
                    </Button>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Інформація про базову версію */}
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                        Базова версія (ліворуч):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2">
                            <strong>Hash:</strong> {baseVersion?.commit_hash?.substring(0, 8)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Автор:</strong> {baseVersion?.author}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Дата:</strong> {formatDateTime(baseVersion?.created_at)}
                        </Typography>
                    </Box>
                    {baseVersion?.description && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Опис:</strong> {baseVersion.description}
                        </Typography>
                    )}
                </Paper>

                {/* Вибір версії для порівняння */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>Версія для порівняння</InputLabel>
                        <Select
                            value={compareVersion?.id || ''}
                            onChange={(e) => handleVersionSelect(e.target.value)}
                            label="Версія для порівняння"
                            disabled={loading}
                        >
                            {loading ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} />
                                </MenuItem>
                            ) : (
                                versions.map((version) => (
                                    <MenuItem key={version.id} value={version.id}>
                                        <Box>
                                            <Typography variant="body2">
                                                {version.commit_hash?.substring(0, 8)} - {version.author}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDateTime(version.created_at)} | {version.description}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                    
                    {compareVersion && (
                        <Button
                            variant="outlined"
                            startIcon={<FaExchangeAlt />}
                            onClick={handleSwapVersions}
                            sx={{ minWidth: '120px' }}
                        >
                            Поміняти
                        </Button>
                    )}
                </Box>

                {/* Інформація про порівнювану версію */}
                {compareVersion && (
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="subtitle2" color="secondary" gutterBottom>
                            Порівнювана версія (праворуч):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2">
                                <strong>Hash:</strong> {compareVersion.commit_hash?.substring(0, 8)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Автор:</strong> {compareVersion.author}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Дата:</strong> {formatDateTime(compareVersion.created_at)}
                            </Typography>
                        </Box>
                        {compareVersion.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Опис:</strong> {compareVersion.description}
                            </Typography>
                        )}
                    </Paper>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Результат порівняння */}
                {renderDiff()}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={() => onClose(null)}
                    variant="contained"
                >
                    Закрити
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PromptComparison;
